import React, { useEffect } from "react"
import cx from "classnames"
import { Collapsible } from "@base-ui/react/collapsible"
import { Popover } from "@base-ui/react/popover"
import { Icon } from "../icon"
import type { IconName } from "../icon/assets/icon-map"
import useLocalStorage from "../use-localstorage"
import { VisuallyHidden } from "../visually-hidden"
import { useNavigationContext } from "./navigation-context"
import { matchesPath, resolveActiveIcon } from "./navigation-utils"
import { useNavigationFlyoutHandle } from "./navigation-flyout"
import { NavigationGroupItemLine } from "./navigation-group-item-line"
import { NavigationBadge, type NavigationBadgeType } from "./navigation-badge"
import { NavigationGroupContext } from "./navigation-group-context"

// =============================================================================
// Props
// =============================================================================

export type NavigationGroupProps = {
  /** Icon shown at all times, including when the sidebar is collapsed */
  icon: IconName
  /** Text label — visible when expanded, screen-reader-only when collapsed */
  label: string
  /** `Navigation.GroupItem` elements */
  children?: React.ReactNode
  /** Whether the group is initially open (uncontrolled) */
  defaultOpen?: boolean
  /** Controlled open state */
  open?: boolean
  /** Called when the group is opened or closed */
  onOpenChange?: (open: boolean) => void
  /**
   * Flags the item as a paid or beta feature displaying an icon
   */
  badge?: NavigationBadgeType
  /**
   * Renders at the right edge of the group trigger, just before the chevron.
   * Hidden when the sidebar is collapsed. Use for counters or other trailing
   * decorations.
   */
  trailing?: React.ReactNode
}

// =============================================================================
// Styles
// =============================================================================

const triggerStyles = [
  // 'group' enables group-aria-[expanded=true] on the chevron
  "group",
  "outline-2 outline-transparent -outline-offset-2 focus-visible:outline-gray-900",
  "rounded-[10px]",
  "min-w-10 w-full h-10",
  "p-2",
  "flex items-center gap-2",
  "text-sm font-semibold text-gray-700",
  "hover:bg-gray-900/[0.06]",
  "transition-colors",
  "data-active:text-gray-900"
].join(" ")

const collapsiblePanelStyles = [
  // content sticks to the bottom of the panel box so it appears to be sliding out from the trigger
  "flex flex-col justify-end",
  // handle hidden="until-found" correctly
  "[&[hidden]:not([hidden='until-found'])]:hidden",
  // --collapsible-panel-height is measured and exposed by BaseUI
  "h-[var(--collapsible-panel-height)] overflow-hidden",
  "transition-[height] ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 duration-150"
].join(" ")

const groupItemListStyles = [
  // this is needed because we have global styles for ul in application.css
  "list-none pt-1 px-0 pb-0 flex flex-col gap-1"
].join(" ")

// =============================================================================
// Component
// =============================================================================

/**
 * An expandable section in the sidebar navigation.
 * Renders a toggle row with an icon, label, and chevron, and reveals
 * `Navigation.GroupItem` children when open.
 *
 * The group handles expand/collapse (uncontrolled or controlled). Active state
 * is derived automatically — if any `Navigation.GroupItem` inside has a `value`
 * that matches `Navigation`'s `currentPath`, the group highlights itself and
 * opens automatically. Pass `defaultOpen` when the group should start open
 * regardless. The `open`/`defaultOpen`/`onOpenChange` props apply only in
 * expanded mode and are ignored when the sidebar is collapsed.
 *
 * In collapsed sidebar mode the group renders only its icon and registers as
 * a trigger for the shared flyout panel owned by `Navigation`. Hovering the
 * icon opens the flyout to the right of the sidebar; moving between group
 * icons re-anchors the same panel rather than reopening it.
 *
 * @example
 * <Navigation.Menu>
 *   <Navigation.Group icon="bolt" label="Automation" defaultOpen>
 *     <Navigation.GroupItem label="Sequences" pathPrefix="/sequences" render={<Link href="/sequences" />} />
 *     <Navigation.GroupItem label="Rules" pathPrefix="/rules" render={<Link href="/rules" />} />
 *   </Navigation.Group>
 * </Navigation.Menu>
 */
export function NavigationGroup({
  icon,
  label,
  children,
  defaultOpen,
  open,
  onOpenChange,
  badge,
  trailing
}: NavigationGroupProps) {
  const { collapsed, currentPath } = useNavigationContext()
  const flyoutHandle = useNavigationFlyoutHandle()
  const [internalOpen, setInternalOpen] = useLocalStorage(
    `navigation:group:${label}:open`,
    defaultOpen ?? false
  )

  const hasActiveChild = React.Children.toArray(children).some(child => {
    if (!React.isValidElement(child)) return false
    const { pathPrefix } = child.props as { pathPrefix?: string }
    return matchesPath(currentPath, pathPrefix)
  })

  useEffect(() => {
    if (hasActiveChild && open === undefined) setInternalOpen(true)
  }, [hasActiveChild, open, setInternalOpen])

  const handleOpenChange = (next: boolean) => {
    if (open === undefined) setInternalOpen(next)
    onOpenChange?.(next)
  }

  // Wrap children so `Navigation.GroupItem` can inherit the group's badge for
  // tooltip callouts.
  const wrappedChildren = (
    <NavigationGroupContext.Provider value={{ inheritedBadge: badge }}>
      {children}
    </NavigationGroupContext.Provider>
  )

  if (collapsed && flyoutHandle) {
    return (
      <li data-slot="navigation-group">
        <Popover.Trigger
          handle={flyoutHandle}
          payload={{ label, badge, items: wrappedChildren }}
          openOnHover
          delay={0}
          closeDelay={50}
          render={<button type="button" />}
          data-slot="navigation-group-trigger"
          data-active={hasActiveChild ? "" : undefined}
          className={cx(triggerStyles, "data-active:bg-gray-900/[0.06]")}
        >
          <Icon
            name={hasActiveChild ? resolveActiveIcon(icon) : icon}
            className="shrink-0"
            aria-hidden
          />
          <VisuallyHidden>{label}</VisuallyHidden>
        </Popover.Trigger>
      </li>
    )
  }

  return (
    <li data-slot="navigation-group">
      <Collapsible.Root
        open={open ?? internalOpen}
        onOpenChange={handleOpenChange}
      >
        <Collapsible.Trigger
          type="button"
          data-slot="navigation-group-trigger"
          data-active={hasActiveChild ? "" : undefined}
          className={triggerStyles}
        >
          <Icon
            name={hasActiveChild ? resolveActiveIcon(icon) : icon}
            className="shrink-0"
            aria-hidden
          />
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="min-w-0 truncate text-left">{label}</span>
            {badge && <NavigationBadge type={badge} />}
          </span>
          {trailing}
          <Icon
            name="down"
            className="shrink-0 transition-transform group-aria-[expanded=true]:-rotate-180"
            aria-hidden
          />
        </Collapsible.Trigger>
        <Collapsible.Panel hiddenUntilFound className={collapsiblePanelStyles}>
          {/* The line sits at left-5 (20px). Each item uses p-2 (8px) +
              half of the size-6 dot container (12px) = 20px, so the dot
              centres exactly on the line. inset-y-5 trims the line to
              span from the first dot centre to the last. */}
          <div className="relative">
            <NavigationGroupItemLine />
            <ul
              className={groupItemListStyles}
              data-slot="navigation-group-items"
            >
              {wrappedChildren}
            </ul>
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </li>
  )
}

NavigationGroup.displayName = "Navigation.Group"
